import { addToBag } from '../bag/bag-services';
import { displayToast } from '../toastify/toastify';
import { handleAjaxRequest } from '../off-canvas-panel/off-canvas-panel';
import { isAny } from '../mobile-detect/mobile-detect';
import { previewQuickbasket, updateBasketTotal, updateQuickBasketCount } from '../quick-basket/quick-basket-ui';
import { setupGroupHelper } from '../group-system/group-system';
import { fetchText } from '../fetch-helper/fetch-helper';
import addDelegateEventListener from '../utility-functions/add-delegate-event-listener';
import panelUpdater from '@remarkablenet/panel-updater';
import formToJSON from '../utility-functions/form-to-json';
import { queryStringFromObject2 } from '../utility-functions/utility-functions';
import { gaTracking } from '../event/event';
import { updateData } from '../update-header/update-header';
import { setCookie, getCookie } from '../cookies/cookies';
import { tableGridSetup, handleTableAddToBag } from './table-buy-grid';
type BuyGridOptionData = {
    productId: string;
    variationids: Array<string>;
};
type BuyGridResponseData = {
    stockId: number;
    stockPreOrderId: number;
    stockVariations: Array<BuyGridResponseStockVariations>;
};
type BuyGridResponseStockVariations = {
    variationValueIds: Array<number>;
    isValid: boolean;
    message: string | null;
    quantity: number;
    status: string;
    stockCode: string;
    stockId: number;
};

const API_BUYGRID_REGION: string = '/product/api/buygrid';

const ADD_TO_BAG_SELECTOR: string = '.js-add-to-bag';
const BUY_GRID_MULTIPLE_CHOICE_SELECTOR: string = 'buy-grid--multiple';
const BUY_GRID_SELECTOR: string = `.buy-grid`;

const SIZE_DROPDOWN_SELECTOR: string = 'buy-grid__size-selector';
const OPTION_ROW_BOX_SELECTOR: string = '.box-selector:not(.js-no-ajax)';
const OPTION_ROW_BOX_SELECTOR_INPUT: string = '.box-selector__value';
const OPTION_ROW_SELECTOR: string = '.buy-grid__option-row';

const OPTION_ROW_GROUP_IDENTIFIER: string = 'buy-grid__option-row--group';
const OPTION_ROW_INPUT_IDENTIFIER: string = 'buy-grid__option-row--input';
const OPTION_ROW_RADIO_IDENTIFIER: string = 'buy-grid__option-row--radio';
const OPTION_ROW_SELECT_IDENTIFIER: string = 'buy-grid__option-row--select';
const OPTION_ROW_SINGLE_IDENTIFIER: string = 'buy-grid__option-row--single';

const BUY_GRID_AUTO_ADD: string = '.buy-grid--auto-add';

const isRadioChecked = (ele: HTMLInputElement) => ele.checked;
const getParentRow = (target: HTMLElement) => <HTMLElement>target.closest(OPTION_ROW_SELECTOR);
const getParentBuyGrid = (target: HTMLElement) => <HTMLFormElement>target.closest(BUY_GRID_SELECTOR);
const isBuyGridMultipleSelect = (buyGrid: HTMLElement) => buyGrid.classList.contains(BUY_GRID_MULTIPLE_CHOICE_SELECTOR);
const isRowGroupBased = (target: HTMLElement) => target.classList.contains(OPTION_ROW_GROUP_IDENTIFIER);
const isRowInputBased = (target: HTMLElement) => target.classList.contains(OPTION_ROW_INPUT_IDENTIFIER);
const isRowRadioBased = (target: HTMLElement) => target.classList.contains(OPTION_ROW_RADIO_IDENTIFIER);
const isRowSelectBased = (target: HTMLElement) => target.classList.contains(OPTION_ROW_SELECT_IDENTIFIER);
const isRowSingleBased = (target: HTMLElement) => target.classList.contains(OPTION_ROW_SINGLE_IDENTIFIER);

const getAllRows = (buyGrid: HTMLElement) => buyGrid.querySelectorAll(OPTION_ROW_SELECTOR) as NodeListOf<HTMLElement>;

const doesTargetHaveVal = (select: HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement) =>
    select.value !== '' && select.value !== undefined;

export const setupBuyGrids = (buyGrids: NodeListOf<HTMLElement>) => {
    buyGrids.forEach((buyGrid) => {
        addbuyGridEventListeners(buyGrid);
        setupGroupHelper(buyGrid, handleGroupIdClick);
        updateBuyGridAvailability(buyGrid).then(() => {
            decidePreOrderState(buyGrid);
        });
    });
    addInsuranceCheck();
};

export const addInsuranceCheck = () => {
    const addInsuranceButtons = document.querySelectorAll<HTMLLinkElement>('.js-add-insurance');
    if (getCookie("noProductInsurance") && addInsuranceButtons) {
        addInsuranceButtons.forEach(btn => {
            const addButton = btn.nextElementSibling as HTMLButtonElement;
            if (addButton) {
                btn.classList.add('d-none');
                addButton.classList.remove('d-none');
            }
        });
    }
};

/**
 * When the users clicks on an element with [group-id] it unticks everything else
 */
const handleGroupIdClick = (data) => {
    const radios = data.groupParent.querySelectorAll('input[name=stockId]');
    radios.forEach(setRadioUnchecked);
};

const addbuyGridEventListeners = (buyGrid: HTMLElement) => {
    addCountrySizeSelectorEventListerners(buyGrid, `.${SIZE_DROPDOWN_SELECTOR}`);
    addBoxSelectorEventListeners(buyGrid, OPTION_ROW_BOX_SELECTOR);
    addAddToBagEventListeners(buyGrid, ADD_TO_BAG_SELECTOR);
    // setUpExtraGiftCardBuyGridEventListeners();
    tableGridSetup();
};

const setRadioUnchecked = (ele: HTMLInputElement) => {
    getRowValidation;
    ele.dataset.waschecked = 'false';
    ele.checked = false;
    ele.removeAttribute('checked');
};

const setRadioChecked = (ele: HTMLInputElement) => {
    ele.dataset.waschecked = 'true';
    ele.checked = true;
    ele.setAttribute('checked', '');
};

const isRadioCheckedInRow = (row: HTMLElement) => {
    const radios = row.querySelectorAll('input[type=radio]');
    const radioArray = Array.from(radios);

    return radioArray.some(isRadioChecked);
};

const isRowValid = (row: HTMLElement) => {
    if (isRowInputBased(row)) {
        const input: HTMLTextAreaElement | HTMLInputElement = row.querySelector('textarea, input');
        if (!input) return;
        return doesTargetHaveVal(input);
    }

    if (isRowRadioBased(row)) {
        const isValid = isRadioCheckedInRow(row);
        return isValid;
    }

    if (isRowSelectBased(row)) {
        const select = row.querySelector('select');
        if (!select) return;
        return doesTargetHaveVal(select);
    }
};

export const isbuyGridValid = (buyGrid: HTMLElement) => {
    const listOfRows = getAllRows(buyGrid);
    const rowsArray = Array.from(listOfRows);
    const results = rowsArray.map(isRowValid);

    return !results.includes(false);
};

export const isBuyGridSingleOptionOnly = (buyGrid: HTMLElement): boolean => {
    const listOfRows = getAllRows(buyGrid);
    const rowsArray = Array.from(listOfRows);
    return rowsArray.every((row: HTMLElement) => isRowSingleBased(row));
};

const getBuyGridValidation = (buyGrid: HTMLElement) => {
    const rows = getAllRows(buyGrid);
    const rowsArray = Array.from(rows);
    const results = rowsArray.filter((row: HTMLElement) => {
        return isRowValid(row) === false;
    });

    return results.map(getRowValidation);
};

const getRowMessage = (row: HTMLElement) => row.dataset.validationMessage;

const getRowValidation = (row: HTMLElement) => {
    return {
        ok: isRowValid(row),
        message: getRowMessage(row),
        reference: row
    } as object;
};

export const handleLetMeKnowRequest = (elm: HTMLInputElement | HTMLElement) => {
    setRadioUnchecked(elm as HTMLInputElement);
    handleAjaxRequest(elm);
};

const isAutoAddToBag = (buyGrid: HTMLElement) => buyGrid.classList.contains(BUY_GRID_AUTO_ADD);

const findAddToBagChild = (buyGrid: HTMLElement) => <HTMLButtonElement>buyGrid.querySelector(ADD_TO_BAG_SELECTOR);

const autoAddToBagCheck = (buyGrid: HTMLElement) => {
    if (isAutoAddToBag(buyGrid)) {
        const addToBagBtn = findAddToBagChild(buyGrid);
        addToBagBtn.click();
        return;
    }
};

const handleBoxSelectorClick = (boxSelectorElm: HTMLElement, buyGrid: HTMLElement) => {
    const row = getParentRow(boxSelectorElm);

    if (!isRowRadioBased(row)) return;

    const boxSelectorOption = boxSelectorElm;
    const radio: HTMLInputElement = boxSelectorOption.querySelector(OPTION_ROW_BOX_SELECTOR_INPUT);
    const rawValue = boxSelectorOption.querySelector('.box-selector__radio-text').innerHTML;
    const value = rawValue.replace(
        /[^0-9.$\xA2-\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20BD\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6]/g,
        ''
    );

    const variation = (<HTMLElement>boxSelectorOption.closest(OPTION_ROW_SELECTOR)).dataset.variation;
    if (variation === 'colour') return;

    if (isOptionState(boxSelectorOption, StockLevels.NOSTOCK) || isOptionState(boxSelectorOption, StockLevels.UNAVAILABLE)) {
        setRadioUnchecked(radio);
        updateBuyGridAvailability(buyGrid);
        displayToast('This item is out of stock.');
        disableAddToBagBtn(buyGrid);
    } else enableAddToBagBtn(buyGrid);

    if (isRadioChecked(radio)) {
        setRadioUnchecked(radio);

        dispatchEvent('buy.grid.item.unselected', {
            buyGrid: buyGrid,
            optionRow: row,
            element: boxSelectorOption,
            value: value,
            inputValue: radio.value
        });

        decidePreOrderState(buyGrid);
        updateBuyGridAvailability(buyGrid);
        return;
    }

    setRadioChecked(radio);
    updateBuyGridAvailability(buyGrid).then((selectedProduct) => {
        decidePreOrderState(buyGrid, selectedProduct?.message);
    });

    dispatchEvent('buy.grid.item.selected', {
        buyGrid: buyGrid,
        optionRow: row,
        element: boxSelectorOption,
        value: value,
        inputValue: radio.value
    });

    autoAddToBagCheck(buyGrid);
};

export const showPreOrder = (buyGrid: HTMLElement, message: string) => {
    const container = buyGrid.querySelector('.js-pre-order');
    container?.classList.remove('hidden');

    const btn: HTMLButtonElement = buyGrid.querySelector('.js-add-to-bag');
    btn.querySelector('span').innerText = 'Pre-order today';

    const timeframe: HTMLElement = buyGrid.querySelector('.js-pre-order-timeframe');
    timeframe.innerText = message;
};

export const hidePreOrder = (buyGrid: HTMLElement) => {
    const container = buyGrid.querySelector('.js-pre-order');
    container?.classList.add('hidden');
};

const rowHasState = (row: HTMLElement, stateClass: Array<string>) => {
    if (row.classList.contains(OPTION_ROW_SINGLE_IDENTIFIER)) {
        return row.classList.contains(stateClass[0]);
    } else {
        const collection = row.querySelectorAll(stateClass[0]);
        return collection.length > 0;
    }
};

export const decidePreOrderState = (buyGrid: HTMLElement, preOrderMessage: string = '') => {
    if (!isbuyGridValid(buyGrid)) {
        hidePreOrder(buyGrid);
        return;
    }

    const listOfRows = getAllRows(buyGrid);
    const rowsArray = Array.from(listOfRows);
    const results = rowsArray.map((row: HTMLElement) => rowHasState(row, StockLevelClasses.STOCKDUE));
    const filteredResults = results.filter((e) => e);

    if (filteredResults.length > 0) {
        showPreOrder(buyGrid, preOrderMessage);
    } else {
        hidePreOrder(buyGrid);
    }
};

export const disableAddToBagBtn = (buyGrid: HTMLElement) => {
    const btn: HTMLButtonElement = buyGrid.querySelector('.js-add-to-bag');
    if (!btn) return;
    btn.classList.add('disabled');
    btn.innerText = 'Select a Size';
};

export const enableAddToBagBtn = (buyGrid: HTMLElement) => {
    const btn: HTMLButtonElement = buyGrid.querySelector('.js-add-to-bag');
    if (!btn) return;
    btn.classList.remove('disabled');
    btn.innerText = 'Add to Basket';
};

export const decideAddToBagBtnState = (buyGrid: HTMLElement) => {
    !isBuyGridSingleOptionOnly
        ? isbuyGridValid(buyGrid)
            ? enableAddToBagBtn(buyGrid)
            : disableAddToBagBtn(buyGrid)
        : null;
};

const dispatchEvent = (eventName: string, data: object) => {
    var event = new CustomEvent(eventName, {
        detail: data
    });

    document.dispatchEvent(event);
};

const showRowValidationMessage = (rowData: object) => displayToast(rowData['message']);

const handleAddToBagClick = (addToBagBtn: HTMLButtonElement) => {
    const buyGrid = getParentBuyGrid(addToBagBtn);
    const gasStrutField = document.querySelector<HTMLInputElement>('.js-gas-strut-force');
    const gasStrutCustomisation = document.querySelector<HTMLInputElement>('.js-gas-strut-customisation');
    const isTableGrid = buyGrid.classList.contains('buy-grid--table');

    if (gasStrutField) {
        const gasStrutForce = parseInt(gasStrutField.value);
        const minVal = parseInt(gasStrutField.getAttribute('min'));
        const maxVal = parseInt(gasStrutField.getAttribute('max'));
        if (gasStrutField.value == "" || gasStrutForce < minVal || gasStrutForce > maxVal) {
            gasStrutField.focus();
            displayToast(`please enter a force between ${minVal}N and ${maxVal}N`);
            return;
        }
    }

    if (addToBagBtn.getAttribute('disabled') === 'disabled') return;

    if (!isbuyGridValid(buyGrid)) {
        const inValidRowData = getBuyGridValidation(buyGrid);
        showRowValidationMessage(inValidRowData[0]);
        return;
    }

    disableAllAddToBagButtons();
    addToBagBtn.innerText = "Adding...";

    const formData = formToJSON(buyGrid);
    if (gasStrutField) formData.gasStrutForce = gasStrutField.value;
    if (gasStrutCustomisation && gasStrutCustomisation.value != "") formData.gasstrutcustomisation = gasStrutCustomisation.value;

    if (!formData.quantity)
        formData.quantity = document.querySelector<HTMLInputElement>('#productQty')?.value || 1;

    if (addToBagBtn.classList.contains('js-without-insurance') && !getCookie('noProductInsurance')) {
        setCookie('noProductInsurance', 1, 1);
        addInsuranceCheck();
    }

    if (isTableGrid) handleTableAddToBag(buyGrid);
    else {
        addToBag(formData).then((data: object) => {
            compileAddToBagData().then((compiledData: object) => {
                let eventData = {
                    area: 'Add to bag - ProductPageBuyGrid, "Clicked"',
                    event: 'Add to Bag',
                    productType: 'product'
                };

                const mergedData: any = Object.assign(eventData, compiledData);

                // --- get search term from URL ---
                const currentUrl = new URL(window.location.href);
                const searchTerm = currentUrl.searchParams.get('search_term');

                const items: any[] = [
                    {
                        item_id: mergedData.productID,
                        item_name: mergedData.productName,
                        quantity: parseInt(formData.quantity),
                        price: parseFloat(mergedData.productValue),
                        item_brand: document.querySelector('.product-info')?.getAttribute('data-brand') || "No brand data available",
                        item_category: document.querySelector<HTMLInputElement>('.js-product-data[name="productCategory"]')?.value || "No category data available",
                        // add search_term if present
                        ...(searchTerm ? { search_term: searchTerm } : {})
                    }
                ];

                gaTracking("add_to_cart", false, {
                    currency: "GBP",
                    value: parseFloat(mergedData.productValue),
                    items: items
                });
            });

            document.querySelector<HTMLButtonElement>('.modal-dialog--add-insurance .modal-close')?.click();
            enableAllAddToBagButtons();
            addToBagBtn.innerText = "Add To Basket";
            handleDefaultAddToBagEvent(buyGrid, data, formData.quantity);
        });
    }    
};

const compileAddToBagData = () => {
    return new Promise((resolve) => {
        const productDataElm: NodeListOf<HTMLInputElement> = document.querySelectorAll('.js-product-data');
        let compiledData: object = {};

        productDataElm.forEach((elm) => {
            const propertyName = elm.getAttribute('name');
            const propertyValue = elm.value;

            compiledData[propertyName] = propertyValue;
        });
        resolve(compiledData);
    });
};

const handleDefaultAddToBagEvent = (buyGrid: HTMLElement, data, qty: string = "1") => {
    if (!data.success) displayToast(data.reason != null ? data.reason : data.message, {
        className: 'toastify-center swatch-danger rounded px-lg-5',
        duration: 1500
    });
    else handleAddToBagSuccess(buyGrid, data, qty);
};

export const handleAddToBagSuccess = (buyGrid: HTMLElement, data, qty: string = "1") => {
    let basketItemCount = parseInt(document.querySelector<HTMLElement>('.quick-basket__count').innerText);
    const newCount = basketItemCount += parseInt(qty);
    updateQuickBasketCount(newCount);
    updateBasketTotal();

    if(!buyGrid.classList.contains('buy-grid--table')) switchButtonText(buyGrid);

    if (!isAny()) {
        if (data.hasLinkedProducts) handleYouMightNeed(data.productsUrl);
        else previewQuickbasket();
    }
    if (isAny()) updateData(true);

    let sucessMessage;
    if (data.message) sucessMessage = data?.message?.includes('added to bag') ? data.message : 'Added to Basket';
    else sucessMessage = 'Added to Basket';
    
    displayToast(sucessMessage, {
        className: 'toastify-center swatch-success rounded px-lg-5',
        duration: 1500
    });
}

const handleYouMightNeed = (url: string) => {
    const link = document.getElementById('youMightNeedLink') as HTMLLinkElement;
    link.setAttribute('href', url);
    link.setAttribute('data-href', url);
    link.click();
    setTimeout(() => {
        const addToBagBtns = document.querySelectorAll('.js-add-extra-to-bag') as NodeListOf<HTMLLinkElement>;
        addToBagBtns?.forEach(btn => {
            btn.addEventListener('click', e => {
                e.preventDefault();
                fetch(btn.href)
                    .then((resp) => resp.json())
                    .then((data) => {
                        btn.classList.add('disabled');
                        if (data.success) btn.innerText = "Added to basket";
                        else btn.innerText = "Error adding to basket";
                    })
                    .catch(function (err) {
                        console.warn('Something went wrong.', err);
                    });
            });
        });
    }, 1000);
};

const handleCountrySizeSelectorClick = async (countrySizeSelect: HTMLSelectElement, buyGridContext: HTMLElement) => {
    const chosenOption = <HTMLOptionElement>countrySizeSelect.querySelector('option:checked');
    const productId = buyGridContext.getAttribute('data-pid');
    const countryCode = chosenOption.value;
    const url = `${API_BUYGRID_REGION}?productid=${productId}&sizingRegion=${countryCode}`;

    const fetchPromise = fetchText(url, null);

    const html = await panelUpdater(buyGridContext, fetchPromise);
    const div = document.createElement('div');

    div.innerHTML = html;
    buyGridContext.innerHTML = div.querySelector('.buy-grid').innerHTML;
};

const addCountrySizeSelectorEventListerners = (buyGridContext: HTMLElement, sizeSelector: string) => {
    addDelegateEventListener(
        buyGridContext,
        'change',
        sizeSelector,

        (event) => {
            event.originalEvent.preventDefault();

            handleCountrySizeSelectorClick(event.delegateTarget as HTMLSelectElement, buyGridContext);

            const allSelectors: NodeListOf<HTMLSelectElement> = document.querySelectorAll(`.${SIZE_DROPDOWN_SELECTOR}`);

            allSelectors.forEach((element) => {
                if (element !== event.delegateTarget) {
                    let thisBuyGridContext: HTMLElement = element.closest(BUY_GRID_SELECTOR);
                    element.value = (event.delegateTarget as HTMLInputElement).value;
                    handleCountrySizeSelectorClick(element, thisBuyGridContext);
                }
            });
        },
        false
    );
};

const addBoxSelectorEventListeners = (buyGridContext: HTMLElement, boxSelectorSelector: string) => {
    addDelegateEventListener(
        buyGridContext,
        'click',
        boxSelectorSelector,
        (event) => {
            const variation = (event.delegateTarget.closest(OPTION_ROW_SELECTOR) as HTMLElement).dataset.variation;
            if (variation === 'colour') return;

            event.originalEvent.preventDefault();

            if (event.delegateTarget.tagName === 'INPUT') return;

            handleBoxSelectorClick(event.delegateTarget, buyGridContext);
        },
        false
    );
};

const addAddToBagEventListeners = (buyGridContext: HTMLElement, addToBagSelector: string) => {
    const addToBagBtns = buyGridContext.querySelectorAll(addToBagSelector);

    addToBagBtns.forEach(() => {
        addDelegateEventListener(
            buyGridContext,
            'click',
            addToBagSelector,
            (event) => {
                event.originalEvent.preventDefault();
                handleAddToBagClick(event.delegateTarget as HTMLButtonElement);
            },
            false
        );
    });
};

const disableAllAddToBagButtons = () => {
    const addToBagBtns = document.querySelectorAll(ADD_TO_BAG_SELECTOR);
    addToBagBtns.forEach(btn => {
        btn.classList.add('disabled');
    });
};


const enableAllAddToBagButtons = () => {
    const addToBagBtns = document.querySelectorAll(ADD_TO_BAG_SELECTOR);
    addToBagBtns.forEach(btn => {
        btn.classList.remove('disabled');
    });
};

const switchButtonText = (buyGrid) => {
    let btnSuccessText = buyGrid.getAttribute('data-btn-success-text');
    let btn = buyGrid.querySelector('.js-add-to-bag');
    if (!btn.getAttribute('data-button-text')) btn.setAttribute('data-button-text', btn.innerText);
    btn.innerText = btnSuccessText ? btnSuccessText : btn.innerText;
    window.setTimeout(() => {
        btn.innerText = btn.getAttribute('data-button-text');
    }, 2500);
};

/**
 * These are server response statuses
 */
const StockLevels = Object.freeze({
    INSTOCK: 'Available',
    LOWSTOCK: 'LowStock',
    NOSTOCK: 'NoStock',
    UNAVAILABLE: 'Unavailable',
    STOCKDUE: 'StockDue'
});

/**
 * These are the classes needed on each js-option-selector to set its state
 */
export const StockLevelClasses = Object.freeze({
    INSTOCK: ['instock'],
    LOWSTOCK: ['lowstock'],
    NOSTOCK: ['nostock', 'disabled'],
    UNAVAILABLE: ['unavailable', 'disabled'],
    STOCKDUE: ['stockdue']
});

export const updateBuyGridAvailability = async (buyGrid) => {
    if (buyGrid.classList.contains('buy-grid--table')) return;
    const url = '/product/product/getbuygrid';
    const listOfRows = [...getAllRows(buyGrid)];
    const variationids = listOfRows
        .filter(
            (row) => (isRowRadioBased(row) && isRadioCheckedInRow(row)) || isRowInputBased(row) || isRowSingleBased(row)
        )
        .map((entry) =>
            isRowRadioBased(entry)
                ? (entry.querySelector(':checked') as HTMLInputElement)?.value
                : isRowSingleBased(entry)
                    ? entry.getAttribute('variation-id')
                    : (entry.querySelector('.box-selector__value') as HTMLInputElement)?.value
    );

    const qsdata: BuyGridOptionData = {
        productId: buyGrid.querySelector('[name="productId"]')?.value || "",
        variationids: variationids
    };
    const qs = queryStringFromObject2(qsdata);

    const response = await fetch(`${url}?${qs}`);
    const responseData: BuyGridResponseData = await response.json();

    responseData.stockVariations.forEach((stock) => {
        const statusKey = getStatusKeyFromValue(stock.status);
        stock.variationValueIds.forEach((variationId) => {
            var singleOptionRow: HTMLElement = buyGrid.querySelector(
                `.${OPTION_ROW_SINGLE_IDENTIFIER}[data-variation-id="${variationId}"]`
            );
            if (singleOptionRow) {
                setOptionState(singleOptionRow, StockLevels[statusKey], buyGrid);
                if (isBuyGridSingleOptionOnly(buyGrid)) setSingleRowAddToBagState(buyGrid, StockLevels[statusKey]);
            } else {
                let boxSelector = null;
                const correctRadioInput = buyGrid.querySelector(
                    `.box-selector__value[value="${variationId}"]`
                ) as HTMLInputElement;
                if (correctRadioInput) boxSelector = correctRadioInput.closest('.box-selector') as HTMLDivElement;
                if (boxSelector != null || boxSelector != undefined) {
                    boxSelector.setAttribute('data-stockid', stock.stockId);
                    setOptionState(boxSelector, StockLevels[statusKey], buyGrid);
                }
            }
        });
    });

    SetDropdownClicks(buyGrid);

    if (responseData.stockId > 0) {
        buyGrid.querySelector('input[name="stockId"]').value = responseData.stockId;
        buyGrid.querySelector('input[name="stockPreOrderId"]').value = responseData.stockPreOrderId;
        decideAddToBagBtnState(buyGrid);
        return responseData.stockVariations[0];
    }
};

const SetDropdownClicks = (buyGrid) => {
    buyGrid.querySelectorAll('.js-quickbuy-dropdown').forEach((inp) => {
        inp.addEventListener('click', (evt) => {
            document.querySelectorAll('.js-quickbuy-dropdown').forEach((x) => {
                if (x.classList.contains('is-visible')) {
                    x.closest('.associated-product').classList.remove('is-visible');
                    x.classList.remove('is-visible');
                }
            });

            inp.classList.add('is-visible');
            inp.closest('.associated-product').classList.add('is-visible');
        });
    });
};

const disableAllOptions = (buyGrid) => {
    const listOfRows = [...getAllRows(buyGrid)];
    const validRows = listOfRows.filter((row) => isRowRadioBased(row));

    validRows.forEach((row) => {
        (<NodeListOf<HTMLElement>>row.querySelectorAll('.js-option-selector')).forEach((elm) =>
            setOptionState(elm, StockLevels.UNAVAILABLE, buyGrid)
        );
    });
};

/**
 * Pass in a StockLevel state and it will return the key
 * Useful for matching up against StockLevelClasses
 *
 * @param {StockLevels} state
 * @returns INSTOCK | LOWSTOCK | NOSTOCK | UNAVAILABLE | STOCKDUE
 */
const getStatusKeyFromValue = (state: string) =>
    Object.entries(StockLevels)
        .filter(([_, value]) => value === state)
        .map((e) => e[0])
        .find(() => true);

const isOptionState = (elm: HTMLElement, state: string) => {
    // Gets the Key name of the passed in state
    const key = getStatusKeyFromValue(state);

    return StockLevelClasses[key].every((c) => elm.classList.contains(c));
};

const setOptionState = (elm: HTMLElement, state: string, buyGrid: HTMLElement) => {
    // reset state first
    Object.keys(StockLevelClasses).forEach((key) => {
        let classEntries = StockLevelClasses[key];
        elm.classList.remove(...classEntries);
    });

    const stockId = parseInt(elm.getAttribute('data-stockId')),
        productId = parseInt(buyGrid.querySelector<HTMLInputElement>('[name="productId"]')!.value);

    elm.removeAttribute('href');

    switch (state) {
        case StockLevels.INSTOCK:
            elm.classList.add(...StockLevelClasses.INSTOCK);
            break;
        case StockLevels.LOWSTOCK:
            elm.classList.add(...StockLevelClasses.LOWSTOCK);
            break;
        case StockLevels.NOSTOCK:
            elm.classList.add(...StockLevelClasses.NOSTOCK, 'js-open-modal');
            elm.setAttribute('href', `/Product/Product/Reminder?stockid=${stockId}&productid=${productId}`);
            elm.setAttribute('data-size', `medium`);
            break;
        case StockLevels.UNAVAILABLE:
            elm.classList.add(...StockLevelClasses.UNAVAILABLE, 'js-open-modal');
            elm.setAttribute('href', `/Product/Product/Reminder?stockid=${stockId}&productid=${productId}`);
            elm.setAttribute('data-size', `medium`);
            break;
        case StockLevels.STOCKDUE:
            elm.classList.add(...StockLevelClasses.STOCKDUE), 'js-open-modal';
            elm.setAttribute('href', `/Product/Product/Reminder?stockid=${stockId}&productid=${productId}`);
            elm.setAttribute('data-size', `medium`);
            break;
    }

    SetDropdownClicks(buyGrid);
};

/**
 * Sets the state of the Add To Bag button when the buy grid is in single row mode
 * you can modify this to set whatever you like the button to be doing
 * @param {string} state the current state of the stock
 * @param {HTMLElement} buyGrid the buy grid element
 * @returns {void}
 */
const setSingleRowAddToBagState = (buyGrid: HTMLElement, state: string): void => {
    const addToBagBtn = buyGrid.querySelector<HTMLElement>('.js-add-to-bag');
    const isOutOfStock = state === StockLevels.NOSTOCK || state === StockLevels.UNAVAILABLE;

    if (!addToBagBtn) return;

    if (isOutOfStock) {
        addToBagBtn.setAttribute('disabled', 'disabled');
        addToBagBtn.innerHTML = addToBagBtn.getAttribute('data-out-of-stock-text');
    } else {
        addToBagBtn.removeAttribute('disabled');
        addToBagBtn.innerHTML = addToBagBtn.getAttribute('data-original-text');
    }
};
